// Domain components — UI/UX §3.1–3.9. These carry the editorial look; screens stay plain.
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Check,
  FALLBACK_NOTIFICATION_ICON,
  Heart,
  Lock,
  NOTIFICATION_ICONS,
} from '../icons/index.js';
import {
  Avatar,
  Button,
  Card,
  Chip,
  StatusPill,
  type StatusTone,
  cx,
} from '../primitives/index.js';

export function FamilyChip({ name }: { name: string }) {
  return <Chip>{name}</Chip>;
}

/** UI/UX §3.1 — the card a member actually contributes from. */
export function PromptCard({
  familyName,
  promptText,
  deadlineLabel,
  urgent,
  submitted,
  draftSaved,
  children,
  onOpen,
  expanded,
}: {
  familyName: string;
  promptText: string;
  deadlineLabel: string;
  urgent?: boolean;
  submitted?: boolean;
  draftSaved?: boolean;
  children?: ReactNode;
  onOpen?: () => void;
  expanded?: boolean;
}) {
  return (
    <Card
      as="article"
      className={cx(
        'relative flex flex-col gap-3',
        submitted && 'roots-submitted border-l-4 border-[var(--color-success)]',
      )}
    >
      {submitted && (
        <span
          aria-hidden
          className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-success)] text-[var(--color-on-accent)] shadow-[var(--shadow-card)]"
        >
          <Check size={16} weight="bold" />
        </span>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FamilyChip name={familyName} />
        {/* §3.1 — the countdown turns terracotta at ≤3 days, neutral before that. */}
        <StatusPill tone={submitted ? 'success' : urgent ? 'active' : 'neutral'}>
          {submitted ? 'Submitted' : deadlineLabel}
        </StatusPill>
      </div>

      <p className="type-body">{promptText}</p>

      {expanded ? (
        children
      ) : (
        <div className="flex items-center justify-between gap-3">
          {draftSaved && <span className="type-caption">Draft saved</span>}
          <Button variant={submitted ? 'secondary' : 'primary'} onClick={onOpen}>
            {submitted ? 'Edit response' : 'Share yours'}
          </Button>
        </div>
      )}
    </Card>
  );
}

/** UI/UX §3.4 — the cover a reader taps to open an edition. */
export function EditionCoverCard({
  theme,
  monthLabel,
  familyName,
  isNew,
  locked,
  onOpen,
}: {
  theme: string | null;
  monthLabel: string;
  familyName: string;
  isNew?: boolean;
  locked?: boolean;
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={locked}
      className={cx(
        'roots-paper roots-cover w-full rounded-[var(--radius-lg)] p-5 text-left shadow-[var(--shadow-card)]',
        locked && 'opacity-70',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="type-label text-[var(--color-text-secondary)]">{familyName}</span>
        {isNew && <StatusPill tone="active">New!</StatusPill>}
        {locked && (
          <Lock size={20} color="var(--color-text-secondary)" aria-label="Locked" role="img" />
        )}
      </div>
      <p className="type-display mt-3">{theme ?? 'This month'}</p>
      <p className="type-caption mt-1">{monthLabel}</p>
      {locked && (
        <p className="type-caption mt-3">Ask a family admin to unlock earlier editions for you.</p>
      )}
    </button>
  );
}

/** UI/UX §3.7 — inbox row; the whole row is the tap target. */
export function NotificationItem({
  type,
  title,
  body,
  timestamp,
  familyName,
  unread,
  onClick,
}: {
  /** Notification Type Registry key (App Flow §2); picks the left icon. */
  type?: string;
  title: string;
  body: string;
  timestamp: string;
  familyName?: string | null;
  unread?: boolean;
  onClick?: () => void;
}) {
  const TypeIcon = (type && NOTIFICATION_ICONS[type]) || FALLBACK_NOTIFICATION_ICON;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-4 text-left shadow-[var(--shadow-card)]"
    >
      <TypeIcon size={24} color="var(--color-text-secondary)" aria-hidden className="shrink-0" />
      <div className="min-w-0 flex-1">
        <p className={cx('type-body', unread && 'font-semibold')}>{title}</p>
        <p className="type-caption mt-1">{body}</p>
        <p className="type-caption mt-1">{timestamp}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        {familyName && <FamilyChip name={familyName} />}
        {unread && (
          <span
            aria-label="Unread"
            className="h-[6px] w-[6px] rounded-full bg-[var(--color-accent-primary)]"
          />
        )}
      </div>
    </button>
  );
}

/** UI/UX §3.8 — the late-joiner card: transparent, never apologetic. */
export function HoldingCard({
  nextMonthName,
  theme,
  submittedCount,
  totalCount,
  occasionTeaser,
  onSetUpProfile,
}: {
  nextMonthName: string;
  theme: string | null;
  submittedCount: number;
  totalCount: number;
  occasionTeaser?: string | null;
  onSetUpProfile?: () => void;
}) {
  return (
    <Card className="flex flex-col gap-3 rounded-[var(--radius-lg)] border-2 border-dashed border-[var(--color-accent-secondary)]">
      <h3 className="type-heading">You’re in!</h3>
      <p className="type-body">Your first prompts start {nextMonthName}.</p>
      {theme && <p className="type-display">{theme}</p>}
      <p className="type-caption">
        Your family is working on it — {submittedCount} of {totalCount} have shared so far
      </p>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: totalCount }).map((_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: anonymous dots have no identity by design
            key={i}
            className={cx(
              'inline-block h-7 w-7 rounded-full',
              i < submittedCount
                ? 'bg-[var(--color-success)]'
                : 'border border-[var(--color-accent-warm-gray)]',
            )}
          />
        ))}
      </div>
      {occasionTeaser && <p className="type-caption">Coming up: {occasionTeaser}</p>}
      <Button variant="accent" onClick={onSetUpProfile}>
        Set up your profile and add your birthday
      </Button>
    </Card>
  );
}

/** UI/UX §3.9 — optimistic heart with the 200ms scale (UI/UX §6). */
export function HeartButton({
  hearted,
  count,
  onToggle,
  label = 'this page',
}: {
  hearted: boolean;
  count: number;
  onToggle: () => void;
  label?: string;
}) {
  const [bumping, setBumping] = useState(false);
  return (
    <button
      type="button"
      aria-label={hearted ? `Remove heart from ${label}` : `Heart ${label}`}
      aria-pressed={hearted}
      onClick={() => {
        setBumping(true);
        setTimeout(() => setBumping(false), 200);
        onToggle();
      }}
      className="flex min-h-[44px] min-w-[44px] flex-col items-center justify-center gap-1"
    >
      <span
        className="inline-flex transition-transform duration-200 ease-out"
        style={{ transform: bumping ? 'scale(1.3)' : 'scale(1)' }}
      >
        <Heart size={24} weight={hearted ? 'fill' : 'regular'} color="var(--color-heart)" />
      </span>
      {count > 0 && <span className="type-caption">{count}</span>}
    </button>
  );
}

export function EmptyState({
  message,
  actions,
}: {
  message: string;
  actions?: ReactNode;
}) {
  return (
    <Card className="flex flex-col items-center gap-4 py-10 text-center">
      <p className="type-body max-w-[36ch]">{message}</p>
      {actions && <div className="flex flex-wrap justify-center gap-2">{actions}</div>}
    </Card>
  );
}

export function MemberRow({
  name,
  avatarUrl,
  membershipRole,
  status,
  isEditor,
  actions,
}: {
  name: string;
  avatarUrl?: string | null;
  /** Named to avoid reading as an ARIA role — this is Membership.role (Permissions §1). */
  membershipRole: 'member' | 'admin';
  status: 'active' | 'holding';
  isEditor?: boolean;
  actions?: ReactNode;
}) {
  const tone: StatusTone = membershipRole === 'admin' ? 'active' : 'neutral';
  return (
    <li className="flex flex-col gap-2 rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-3">
      <div className="flex items-center gap-3">
        <span className="shrink-0">
          <Avatar name={name} src={avatarUrl} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="type-body truncate">{name}</p>
          <p className="type-caption">{status === 'holding' ? 'Joining next month' : 'Member'}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isEditor && <StatusPill tone="warning">Editor</StatusPill>}
          {membershipRole === 'admin' && <StatusPill tone={tone}>Admin</StatusPill>}
        </div>
      </div>
      {/* Admin actions sit on their own row so names never get squeezed (§4.2 targets). */}
      {actions && <div className="flex flex-wrap justify-end gap-1">{actions}</div>}
    </li>
  );
}
